import type { SignatureRequestTemplateData } from "@/lib/data/requests/contacts/contact-templated-email";
import type { CSSProperties } from "react";

interface SignatureRequestEmailPreviewProps {
  templateData: SignatureRequestTemplateData;
}

const outerTableStyle: CSSProperties = {
  width: "100%",
  background: "#f6f9fc",
  padding: "24px 0",
};

const innerTableStyle: CSSProperties = {
  width: "560px",
  background: "#ffffff",
  borderRadius: "10px",
  boxShadow: "0 2px 8px rgba(46, 41, 51, 0.08)",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
  color: "#1f2937",
};

const sectionPaddingStyle: CSSProperties = {
  padding: "0 32px 18px",
};

const paragraphStyle: CSSProperties = {
  margin: "0 0 16px",
  lineHeight: 1.6,
};

const infoBoxStyle: CSSProperties = {
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "20px",
};

const buttonStyle: CSSProperties = {
  display: "inline-block",
  background: "#2563eb",
  color: "#ffffff",
  textDecoration: "none",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: 600,
};

const footerNoteStyle: CSSProperties = {
  margin: 0,
  fontSize: "12px",
  color: "#9ca3af",
};

export const SignatureRequestEmailPreview = ({
  templateData,
}: SignatureRequestEmailPreviewProps) => {
  const {
    document_name: documentName,
    document_id: documentId,
    due_date: dueDate,
    unique_password: uniquePassword,
    signature_link: signatureLink,
    expiration_date: expirationDate,
    sender_name: senderName,
    firm_name: firmName,
    recipient_name: recipientName,
  } = templateData;
  const safeSignatureLink = signatureLink || "#";
  return (
    <div style={{ background: "#f6f9fc", padding: "24px 0" }}>
      <table
        role="presentation"
        style={outerTableStyle}
        cellSpacing={0}
        cellPadding={0}
      >
        <tbody>
          <tr>
            <td align="center">
              <table
                role="presentation"
                style={innerTableStyle}
                cellSpacing={0}
                cellPadding={0}
              >
                <tbody>
                  <tr>
                    <td style={{ padding: "28px 32px", textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: "#111827",
                          marginBottom: "6px",
                        }}
                      >
                        LawWorks
                      </div>
                      <div style={{ fontSize: "14px", color: "#6b7280" }}>
                        Electronic signature request
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td style={sectionPaddingStyle}>
                      <p style={paragraphStyle}>
                        Dear {recipientName || "Recipient"},
                      </p>
                      <p style={paragraphStyle}>
                        Your electronic signature is required for the following
                        document:
                      </p>
                    </td>
                  </tr>

                  <tr>
                    <td style={{ padding: "0 32px 24px" }}>
                      <div style={infoBoxStyle}>
                        <div style={{ marginBottom: "12px" }}>
                          <strong style={{ color: "#374151" }}>
                            Document:
                          </strong>{" "}
                          <span style={{ color: "#1f2937" }}>
                            {documentName || ""}
                          </span>
                        </div>
                        <div style={{ marginBottom: "12px" }}>
                          <strong style={{ color: "#374151" }}>
                            Document ID:
                          </strong>{" "}
                          <span style={{ color: "#1f2937" }}>
                            {documentId || ""}
                          </span>
                        </div>
                        <div>
                          <strong style={{ color: "#374151" }}>
                            Due date:
                          </strong>{" "}
                          <span style={{ color: "#1f2937" }}>
                            {dueDate || ""}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td style={sectionPaddingStyle}>
                      <p style={paragraphStyle}>To sign the document:</p>
                      <ol
                        style={{
                          margin: "0 0 16px",
                          paddingLeft: "20px",
                          lineHeight: 1.6,
                        }}
                      >
                        <li style={{ marginBottom: "8px" }}>
                          Click the button below
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                          Enter your unique password:{" "}
                          <strong style={{ color: "#111827" }}>
                            {uniquePassword || ""}
                          </strong>
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                          Review and sign the document
                        </li>
                      </ol>
                    </td>
                  </tr>

                  <tr>
                    <td
                      style={{
                        padding: "0 32px 24px",
                        textAlign: "center",
                      }}
                    >
                      <a href={safeSignatureLink} style={buttonStyle}>
                        Sign document now
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td
                      style={{
                        padding: "0 32px 18px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          lineHeight: 1.6,
                          color: "#6b7280",
                          fontStyle: "italic",
                        }}
                      >
                        This link will expire on {expirationDate || ""}.
                      </p>
                    </td>
                  </tr>

                  <tr>
                    <td style={sectionPaddingStyle}>
                      <p style={paragraphStyle}>
                        If you have any questions, please contact us.
                      </p>
                      <p style={{ margin: 0, lineHeight: 1.6 }}>
                        Best regards,
                        <br />
                        <strong style={{ color: "#111827" }}>
                          {senderName || ""}
                        </strong>
                        <br />
                        <span style={{ color: "#6b7280" }}>
                          {firmName || ""}
                        </span>
                      </p>
                    </td>
                  </tr>

                  <tr>
                    <td style={{ padding: "0 32px 32px" }}>
                      <hr
                        style={{
                          border: "none",
                          borderTop: "1px solid #e5e7eb",
                          margin: "0 0 14px",
                        }}
                      />
                      <p style={footerNoteStyle}>
                        Sent by LawWorks &bull; Please don&apos;t reply to this
                        automated message.
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                style={{
                  fontSize: "12px",
                  color: "#9ca3af",
                  marginTop: "10px",
                }}
              >
                Having trouble? Email info@lawworks.com for assistance.
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
